import type {
    START_DATE,
    END_DATE,
} from './constants'
import type { Moment } from 'moment'
import type { AxiosResponse } from 'axios'

export type GarageReservationTimeRange = {
    beginsAt: string,
    endsAt: string,
    minimumStayApplied: boolean,
}

export type Garage = {
    id: number,
    name: string,
    reservationSlug?: string | null,
    amountCentsCzk: number | null,
    amountCentsHourlyCzk: number | null,
    amountCentsEur: number | null,
    amountCentsHourlyEur: number | null,
    height: number | null,
    length: number | null,
    weight: number | null,
    width: number | null,
    distance: number,
    image: string,
    available: boolean,
    timeRangeValid: boolean | null,
    availabilityErrors: string[],
    lat: number,
    lng: number,
    minimumStayHours: number,
    reservationTimeRange: GarageReservationTimeRange | null,
    priceLoading: boolean,
    checkLaterAvailability: boolean,
    lastPlacesAvailable?: boolean,
    showPopup: boolean,
    popupText: string,
    electricCarNotAllowed: boolean,
    lpgCngNotAllowed: boolean,
    motorbikesNotAllowed: boolean,
    parkingLot: boolean,
}

export enum SortBy {
    distance = "distance",
    price = "price",
}

export enum ParkingType {
    all = "all",
    garage = "garage",
    parkingLot = "parkingLot",
}

export type FiltersType = {
    minPrice: number | null,
    maxPrice: number | null,
    minHeight: number | null,
    minWidth: number | null,
    minLength: number | null,
    minWeight: number | null,
    parkingType: ParkingType,
    sort: SortBy,
    electricAllowed: boolean,
    lpgCngAllowed: boolean,
    motorbikesAllowed: boolean,
    showAvailable: boolean,
}

export type MinMaxRange = {
    min: number,
    max: number,
}

export type HoverPoint = {
    id: number,
    lat: number,
    lng: number,
}

type PointOrigin = {
    fromList?: boolean,
}

export type SelectedPoint = HoverPoint & PointOrigin

export type SearchType = {
    startDate: Moment | null,
    startTime: string | null,
    endDate: Moment | null,
    endTime: string | null,
    location: string | null,
    focusedInput: typeof START_DATE | typeof END_DATE,
}

export type TimeHighlight = {
    startTime: boolean,
    endTime: boolean,
}

export type GarageAvailability = {
    garages: Array<GarageLaterAvailability>,
    options: Array<LaterAvailabilityOption>,
    searchedStartTime: string | null,
    searchKey: string,
    onSelectStartTime: (startTime: LaterAvailabilityTime) => void,
}

export type LaterAvailabilityTime = '16:00' | '17:00' | '18:00'

export type GarageLaterAvailability = {
    garageId: number,
    availableTimes: Array<LaterAvailabilityTime>,
    earliestAvailableTime: LaterAvailabilityTime,
}

export type LaterAvailabilityOption = {
    startTime: LaterAvailabilityTime,
    garageIds: Array<number>,
    diff: number,
}

export enum DocumentTypes {
    ticket = "ticket",
    taxCorrection = "tax-correction",
}

export type Document = {
    id: number,
    data_file_name: string,
    url: string,
}

export type Gate = {
    id: number,
    gateClass: GateClass,
    isVisible: boolean,
    name: {
        cs: string | null,
        en: string | null,
        de: string | null,
        pl: string | null,
    },
}

export type GateConfirmation = {
    id: number | null,
    gateClass: GateClass | null,
}

export type FinalUnpaidAmountType =
    | 'none'
    | 'unfinished_reservation'
    | 'prolongation'
    | 'change_unit'

export type Reservation = {
    id: number,
    status: ReservationStatuses,
    garageId: number,
    garageSlug?: string | null,
    garageName: string,
    garagePlace: string,
    garage: {
        id: number,
        street: string,
        city: string,
        lat: string,
        lng: string,
        garage_translations: {
            locale: string
            application_note: string
        }[]
    }
    driverFullName: string,
    driverEmail: string,
    licensePlate: string,
    phoneNumber1: string,
    phoneNumber2: string | null,
    startDate: string,
    endDate: string,
    createdAt: string,
    price: number,
    token: string,
    unpaidUrl: string | null,
    finalUnpaidAmountType: FinalUnpaidAmountType,
    unpaidAmount: {
        cents: number,
        currency: string,
    },
    entrance?: Gate,
    exit?: Gate,
    receptions?: Array<Gate>,
    invoices: Array<Document>,
    tickets: Array<Document>,
    invoiceCorrections: Array<Document>,
    creditNotes: Array<Document>,
    terms_of_services: Document,
    privacy_policy: Document,
    garage_tos: Document,
    change_unit_process_actual: {
        id: number,
        successful_at: string | null,
        failed_at: string | null,
    } | null,
}

export enum ReservationStatuses {
    booked = "booked",
    current = "current",
    elapsed = "elapsed",
    canceled = "canceled",
    allocated = "allocated",
}

export enum ReservationStatusFilters {
    all = "all",
    active = "active",
    elapsed = "elapsed",
    canceled = "canceled",
    allocated = "allocated",
}

export enum ReservationStatusesFromAPI {
    booked = "booked",
    canceled = "canceled",
    allocated = "allocated",
    expired = "expired",
}

export enum ReservationsSortBy {
    id = "id",
    startAt = "startAt",
}

export type ReservationsFiltersType = {
    status: ReservationStatusFilters,
    sort: ReservationsSortBy,
}

export type ReservationForm = {
    phoneNumber1: string | null,
    phoneNumber2: string | null,
    numberPlate: string | null,
}

export enum GateClass {
    entrance = "GarageEntrance",
    exit = "GarageExit",
    reception = "ReceptionDoor",
}

export enum ExtensionQuery {
    request = "prolong_request",
    submit = "prolong_submit",
}

export type ReservationExtensionResponse = {
    query: ExtensionQuery,
    message: string | null,
    standardExtensionPriceCents?: number,
    extensionPriceCents: number,
    url: string | undefined,
    errors: Array<string>,
}

export enum Currency {
    czk = "czk",
    eur = "eur",
}

export type SearchSubmissionType = {
    startDate: Moment | null
    startTime: string | null
    endDate: Moment | null
    endTime: string | null
    location: string | null
}

export interface ErrorResponse {
    error: string
}

export interface ErrorArrayResponse {
    errors: string[]
}

export interface MutationOptions {
    closeCallback?: () => void
    onSuccessCallback?: (arg1: AxiosResponse['data']) => void
    setRequestInProgress: (arg1: boolean) => void
    onErrorCallback?: (arg1: string) => void
}

export enum VehicleEntryMode {
    now = "now",
    later = "later",
}

export interface UserVehicle {
    id: number
    name: string
    number_plate: string
    color: string
}
